<script setup lang="ts">
/*
* Модалка создания/редактирования полезной ссылки (админка, право `links.edit`)
*/
import { useDisplay } from 'vuetify/framework'

import CustomCloseButton from '@/components/createdUi/CustomCloseButton.vue'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import { useDialogDismissGuard } from '@/composables/useDialogDismissGuard'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { useLinksSettingsStore } from '@/modules/links/stores/linksSettingsStore'
import type { IUsefulLink } from '@/modules/links/types/IUsefulLink'
import AvatarCropDialog from '@/modules/user/components/AvatarCropDialog.vue'
import emptyLink from '@images/links/emptyLink.jpg'
import profile_delete from '@images/icons/profile_delete.svg'
import profile_download from '@images/icons/profile_download.svg'

const props = withDefaults(
  defineProps<{
    modelValue?: boolean
    link?: IUsefulLink | null
  }>(),
  {
    modelValue: false,
    link: null,
  },
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
}>()

const { xs } = useDisplay()
const feedbackStore = useFeedbackStore()
const linksSettingsStore = useLinksSettingsStore()

const isModalOpen = computed({
  get: () => props.modelValue,
  set: (val: boolean) => {
    if (!val)
      onDialogDismissAttempt()
    else
      emit('update:modelValue', true)
  },
})

const isEditMode = computed(() => !!props.link?.id)

const name = ref('')
const url = ref('')
const description = ref('')
const isActive = ref(true)
const logoFile = ref<File | null>(null)
/** Пользователь удалил текущий логотип — при сохранении уйдёт `logo: null` */
const logoCleared = ref(false)
const logoPreview = ref('')
const logoInput = ref<HTMLInputElement>()

const isCropDialogOpen = ref(false)
const cropImageSrc = ref('')
const cropFileName = ref('logo.jpg')
let cropObjectUrl: string | null = null
let previewObjectUrl: string | null = null

const cleanupCrop = () => {
  if (cropObjectUrl) {
    URL.revokeObjectURL(cropObjectUrl)
    cropObjectUrl = null
  }
  cropImageSrc.value = ''
  if (logoInput.value) logoInput.value.value = ''
}

const setPreview = (src: string) => {
  if (previewObjectUrl) {
    URL.revokeObjectURL(previewObjectUrl)
    previewObjectUrl = null
  }
  logoPreview.value = src
}

onBeforeUnmount(() => {
  cleanupCrop()
  setPreview('')
})

const triggerLogoInput = () => {
  logoInput.value?.click()
}

const handleLogoUpload = (event: Event) => {
  const file = (event.target as HTMLInputElement).files?.[0]
  if (!file) return
  cleanupCrop()
  cropFileName.value = file.name
  cropObjectUrl = URL.createObjectURL(file)
  cropImageSrc.value = cropObjectUrl
  isCropDialogOpen.value = true
}

const onCropConfirm = (file: File) => {
  cleanupCrop()
  logoFile.value = file
  logoCleared.value = false
  setPreview(URL.createObjectURL(file))
  previewObjectUrl = logoPreview.value
}

const handleDeleteLogo = () => {
  logoFile.value = null
  logoCleared.value = !!props.link?.logo
  setPreview('')
}

const showUnsavedChangesDialog = ref(false)
const initialFormSnapshot = ref('')

const getFormSnapshot = () => JSON.stringify({
  name: name.value.trim(),
  url: url.value.trim(),
  description: description.value.trim(),
  isActive: isActive.value,
  logo: logoFile.value?.name ?? null,
  logoCleared: logoCleared.value,
})

watch(
  [() => props.modelValue, () => props.link],
  ([isOpen, link]) => {
    if (!isOpen) return
    name.value = link?.name ?? ''
    url.value = link?.url ?? ''
    description.value = link?.description ?? ''
    isActive.value = link?.is_active ?? true
    logoFile.value = null
    logoCleared.value = false
    setPreview(link?.logo ?? '')
    initialFormSnapshot.value = getFormSnapshot()
  },
  { immediate: true },
)

const closeModalDirectly = () => {
  emit('update:modelValue', false)
}

const handleCloseModal = () => {
  if (getFormSnapshot() !== initialFormSnapshot.value)
    showUnsavedChangesDialog.value = true
  else
    closeModalDirectly()
}

const onDialogDismissAttempt = useDialogDismissGuard(handleCloseModal)

const handleCloseConfirm = (value: boolean) => {
  showUnsavedChangesDialog.value = false
  if (value) closeModalDirectly()
}

const handleSave = async () => {
  if (!name.value.trim() || !url.value.trim()) {
    feedbackStore.showToast({
      type: 'error',
      message: 'Укажите название и адрес ссылки',
      time: 3000,
    })
    return
  }

  const payload = {
    name: name.value.trim(),
    url: url.value.trim(),
    description: description.value.trim(),
    is_active: isActive.value,
    logo: logoFile.value ?? (logoCleared.value ? null : undefined),
  }

  const isSaved = isEditMode.value && props.link
    ? await linksSettingsStore.updateLink(props.link.id, payload)
    : await linksSettingsStore.createLink(payload)

  if (isSaved) closeModalDirectly()
}
</script>

<template>
  <VDialog
    v-model="isModalOpen"
    max-width="700"
    persistent
    content-class="entity-form-menu"
    @click:outside="onDialogDismissAttempt"
    @keydown.esc.stop="onDialogDismissAttempt"
  >
    <VCard>
      <div class="d-flex align-center justify-space-between" :class="xs ? 'px-6 py-4' : 'pa-6'">
        <span class="text-h4">
          {{ isEditMode ? 'Редактирование ссылки' : 'Создание ссылки' }}
        </span>
        <CustomCloseButton :close-action="onDialogDismissAttempt" />
      </div>

      <div class="d-flex flex-column gap-5 px-6 pb-6">
        <div class="d-flex align-center gap-3">
          <VAvatar
            color="primary"
            variant="tonal"
            size="100"
            rounded="circle"
          >
            <VImg
              :src="logoPreview || emptyLink"
              contain
            />
          </VAvatar>

          <div class="d-flex flex-column">
            <VBtn
              variant="plain"
              :ripple="false"
              class="cursor-pointer pa-0"
              :append-icon="profile_download"
              color="secondary"
              @click="triggerLogoInput"
            >
              Загрузить
              <input
                ref="logoInput"
                type="file"
                accept="image/*"
                class="d-none"
                @change="handleLogoUpload"
              >
            </VBtn>

            <VBtn
              v-if="logoPreview"
              variant="plain"
              :ripple="false"
              class="cursor-pointer pa-0"
              :append-icon="profile_delete"
              color="secondary"
              @click="handleDeleteLogo"
            >
              Удалить
            </VBtn>
          </div>
        </div>

        <VTextarea
          v-model="name"
          variant="outlined"
          clearable
          placeholder="Введите название"
          persistent-placeholder
          rows="1"
          auto-grow
          no-resize
          hide-details="auto"
        >
          <template #label>
            <span>Название<sup class="required-label">*</sup></span>
          </template>
        </VTextarea>

        <VTextField
          v-model="url"
          variant="outlined"
          clearable
          placeholder="https://"
          persistent-placeholder
          hide-details="auto"
        >
          <template #label>
            <span>Адрес ссылки<sup class="required-label">*</sup></span>
          </template>
        </VTextField>

        <VTextarea
          v-model="description"
          label="Описание"
          variant="outlined"
          density="comfortable"
          rows="3"
          persistent-placeholder
          hide-details="auto"
        />
        <div class="d-flex align-center gap-4" :class="xs ? 'flex-column' : 'justify-space-between'">
          <VSwitch
            v-model="isActive"
            label="Ссылка активна"
            color="primary"
            hide-details
            density="compact"
            inset
          />
          <VBtn
            :block="xs"
            color="primary"
            @click="handleSave"
          >
            Сохранить
          </VBtn>
        </div>
      </div>
    </VCard>
  </VDialog>

  <ConfirmDialog
    v-model:is-dialog-visible="showUnsavedChangesDialog"
    confirmation-question="Закрыть без сохранения?"
    confirmation-text="Изменения ссылки не сохранятся"
    confirm-btn="Закрыть"
    reject-btn="Отменить"
    @confirm="handleCloseConfirm"
  />

  <AvatarCropDialog
    v-model="isCropDialogOpen"
    :image-src="cropImageSrc"
    :file-name="cropFileName"
    @confirm="onCropConfirm"
    @cancel="cleanupCrop"
  />
</template>
